import { compare } from 'fast-json-patch';
import { scanRootHasScopeChanges } from './changeOrderLib';
import { stripUpdatedAt } from './inspectLib';
import { Scope } from './OptimizedValueExpressions';
import { ScanPhoto } from './ScanRoot';
import {
  getDeoptimizedScan,
  getOptimizedScan,
  getScanGroup,
  getScanTask,
  getScanWithUpdatedGroup,
  getScanWithUpdatedOption,
  getScanWithUpdatedTask,
  getScopeForScanPartChildren,
  getUniqueScanPhotos,
  getUpdatedGroup,
  getUpdatedLineItem,
  getUpdatedOption,
  getUpdatedScan,
  getUpdatedTask,
  ScanGroupOptimized,
  ScanLineItemOptimized,
  ScanOptionOptimized,
  ScanPartOptimized,
  ScanRootOptimized,
  ScanTaskOptimized,
} from './ScanRootOptimized';

/**
 * Finds photos that exist in beforePhotos but not in afterPhotos
 */
const getMissingPhotos = (beforePhotos: ScanPhoto[], afterPhotos: ScanPhoto[]): ScanPhoto[] => {
  const afterPhotoIds = new Set(afterPhotos.map(p => p.photoId));
  return beforePhotos.filter(p => !afterPhotoIds.has(p.photoId));
};

const getRevertScanPartUpdates = (baselineScanPart: ScanPartOptimized) => ({
  values: baselineScanPart.values,
  notApplicable: baselineScanPart.notApplicable ?? null,
  tags: baselineScanPart.tags ?? null,
  skuId: baselineScanPart.skuId ?? null,
  skuDetails: baselineScanPart.skuDetails ?? null,
});
/**
 * Reverts a line item's scope changes back to baseline.
 * - Reverts all values back to baseline
 * - Reverts the product (skuId, skuDetails) back to baseline
 */
export const getScopeRevertedLineItem = (
  groupId: string,
  taskId: string,
  optionId: string,
  lineItem: ScanLineItemOptimized,
  baselineLineItem: ScanLineItemOptimized,
  scopeForLineItem: Scope,
  now?: number,
): ScanLineItemOptimized =>
  getUpdatedLineItem(
    groupId,
    taskId,
    optionId,
    lineItem,
    getRevertScanPartUpdates(baselineLineItem),
    scopeForLineItem,
    now,
  );

/** A part's children reverted to baseline: the baseline's children, each one changed since reverted by `revertChild`. */
const getScopeRevertedChildren = <Child extends ScanPartOptimized>(
  childrenById: Record<string, Child>,
  baselineChildren: Child[] | undefined,
  revertChild: (child: Child, baselineChild: Child) => Child,
): Child[] | undefined =>
  baselineChildren?.map(baselineChild => {
    const child: Child | undefined = childrenById[baselineChild.id];
    return child && child !== baselineChild ? revertChild(child, baselineChild) : baselineChild;
  });

/**
 * A part's children with one child reverted to baseline: taken out when the baseline doesn't have it, put back when it
 * was deleted, otherwise reverted in place by `revertChild`. The same children when that changes nothing.
 */
const getChildrenWithScopeRevertedChild = <Child extends ScanPartOptimized>(
  children: Child[],
  childId: string,
  baselineChild: Child | undefined,
  revertChild: (child: Child, baselineChild: Child) => Child,
): Child[] => {
  const child = children.find(({ id }) => id === childId);
  if (!child) return baselineChild ? [...children, baselineChild] : children;
  if (!baselineChild) return children.filter(({ id }) => id !== childId);
  const revertedChild = revertChild(child, baselineChild);
  return revertedChild === child ? children : children.map(sibling => sibling.id === childId ? revertedChild : sibling);
};

/**
 * Reverts an option's scope changes back to baseline.
 * - Reverts all values back to baseline
 * - Reverts line items structure (restores deleted, removes added)
 * - Recursively reverts changed line items
 */
export const getScopeRevertedOption = (
  groupId: string,
  taskId: string,
  option: ScanOptionOptimized,
  baselineOption: ScanOptionOptimized,
  scopeForOption: Scope,
  now?: number,
): ScanOptionOptimized => {
  const scopeForChildren = getScopeForScanPartChildren(option);
  return getUpdatedOption(
    groupId,
    taskId,
    option,
    {
      ...getRevertScanPartUpdates(baselineOption),
      lineItems: getScopeRevertedChildren(
        option.lineItemsById,
        baselineOption.lineItems,
        (lineItem, baselineLineItem) => getScopeRevertedLineItem(groupId, taskId, option.id, lineItem, baselineLineItem, scopeForChildren, now),
      ) ?? null,
    },
    scopeForOption,
    now,
  );
};

/**
 * Reverts a task's scope changes back to baseline.
 * - Reverts assignedContractorId back to baseline
 * - Reverts all values back to baseline
 * - Reverts options structure (restores deleted, removes added)
 * - Recursively reverts changed options
 */
export const getScopeRevertedTask = (
  groupId: string,
  task: ScanTaskOptimized,
  baselineTask: ScanTaskOptimized,
  scopeForTask: Scope,
  now?: number,
): ScanTaskOptimized => {
  const scopeForChildren = getScopeForScanPartChildren(task);
  // Only include assignedContractorId if it changed; use null to clear if baseline was undefined
  const contractorChanged = task.assignedContractorId !== baselineTask.assignedContractorId;
  const assignedContractorId = contractorChanged ? (baselineTask.assignedContractorId ?? null) : undefined;

  return getUpdatedTask(
    groupId,
    task,
    {
      ...getRevertScanPartUpdates(baselineTask),
      assignedContractorId,
      options: getScopeRevertedChildren(
        task.optionsById,
        baselineTask.options,
        (option, baselineOption) => getScopeRevertedOption(groupId, task.id, option, baselineOption, scopeForChildren, now),
      ) ?? null,
    },
    scopeForTask,
    now,
  );
};

/**
 * Reverts a task's scope changes back to baseline: removes it when the baseline doesn't have it, restores it when it
 * was deleted, and otherwise reverts it in place (getScopeRevertedTask).
 * @param scan - The scan to revert the task in
 * @param baselineScan - The baseline scan to revert to
 * @param groupId - The task's group
 * @param taskId - The task to revert, in the scan, the baseline, or both
 * @param now - The current time to use for the revert
 * @returns A new scan with the task reverted, or the same scan when there's nothing to revert
 */
export const getScanWithRevertedTask = (
  scan: ScanRootOptimized,
  baselineScan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  now?: number,
): ScanRootOptimized => {
  const group = getScanGroup(scan, groupId);
  const scopeForTask = getScopeForScanPartChildren(group);
  const tasks = getChildrenWithScopeRevertedChild(
    group.tasks,
    taskId,
    baselineScan.groupsById[groupId]?.tasksById[taskId],
    (task, baselineTask) => getScopeRevertedTask(groupId, task, baselineTask, scopeForTask, now),
  );
  return tasks === group.tasks ? scan : getScanWithUpdatedGroup(scan, groupId, { tasks }, now);
};

/**
 * Reverts one line item's scope changes back to baseline: the line item's share of reverting its task. Removes it
 * when the baseline doesn't have it, restores it when it was deleted (with its option, holding only this line item,
 * when that was deleted too), and otherwise reverts it in place.
 * @param scan - The scan to revert the line item in
 * @param baselineScan - The baseline scan to revert to
 * @param groupId - The line item's group
 * @param taskId - The line item's task
 * @param lineItemId - The line item to revert, in the scan, the baseline, or both
 * @param now - The current time to use for the revert
 * @returns A new scan with the line item reverted, or the same scan when there's nothing to revert
 */
export const getScanWithRevertedLineItem = (
  scan: ScanRootOptimized,
  baselineScan: ScanRootOptimized,
  groupId: string,
  taskId: string,
  lineItemId: string,
  now?: number,
): ScanRootOptimized => {
  const task = getScanTask(scan, groupId, taskId);
  const getOptionWithLineItem = (options: ScanOptionOptimized[] = []) => options.find(option => option.lineItemsById[lineItemId]);
  const baselineOption = getOptionWithLineItem(baselineScan.groupsById[groupId]?.tasksById[taskId]?.options);
  const option = getOptionWithLineItem(task.options) ?? (baselineOption && task.optionsById[baselineOption.id]);

  if (option) {
    const scopeForLineItem = getScopeForScanPartChildren(option);
    const lineItems = getChildrenWithScopeRevertedChild(
      option.lineItems,
      lineItemId,
      baselineOption?.lineItemsById[lineItemId],
      (lineItem, baselineLineItem) => getScopeRevertedLineItem(groupId, taskId, option.id, lineItem, baselineLineItem, scopeForLineItem, now),
    );
    return lineItems === option.lineItems ? scan : getScanWithUpdatedOption(scan, groupId, taskId, option.id, { lineItems }, now);
  }
  if (!baselineOption) return scan;
  const optionWithOnlyLineItem = getUpdatedOption(
    groupId,
    taskId,
    baselineOption,
    { lineItems: [baselineOption.lineItemsById[lineItemId]] },
    getScopeForScanPartChildren(task),
    now,
  );
  return getScanWithUpdatedTask(scan, groupId, taskId, { options: [...task.options, optionWithOnlyLineItem] }, now);
};

/**
 * Reverts a group's scope changes back to baseline.
 * - Reverts all values back to baseline
 * - Reverts tasks structure (restores deleted, removes added)
 * - Recursively reverts changed tasks
 * - Preserves photos from removed children by adding them to the group
 */
export const getScopeRevertedGroup = (
  group: ScanGroupOptimized,
  baselineGroup: ScanGroupOptimized,
  scopeForGroup: Scope,
  now?: number,
): ScanGroupOptimized => {
  const scopeForChildren = getScopeForScanPartChildren(group);
  const allPhotosBefore = group.optimized.allPhotos;

  // First, revert the scope changes
  let revertedGroup = getUpdatedGroup(
    group,
    {
      ...getRevertScanPartUpdates(baselineGroup),
      tasks: getScopeRevertedChildren(
        group.tasksById,
        baselineGroup.tasks,
        (task, baselineTask) => getScopeRevertedTask(group.id, task, baselineTask, scopeForChildren, now),
      ) ?? null,
    },
    scopeForGroup,
    now,
  );

  // Check if any photos were lost during revert (from removed tasks/options/lineItems)
  const allPhotosAfter = revertedGroup.optimized.allPhotos;
  const missingPhotos = getMissingPhotos(allPhotosBefore, allPhotosAfter);

  // If photos were lost, add them to the group's photos
  if (missingPhotos.length > 0) {
    revertedGroup = getUpdatedGroup(
      revertedGroup,
      {
        photos: getUniqueScanPhotos(revertedGroup.photos, missingPhotos),
      },
      scopeForGroup,
      now,
    );
  }

  return revertedGroup;
};

/**
 * Reverts an entire scan's scope changes back to baseline.
 * This is the main entry point for reverting change orders.
 *
 * Reverts:
 * - defaultContractorsByTrade back to baseline
 * - All values back to baseline
 * - Groups structure (restores deleted, removes added)
 * - Recursively reverts all changed groups, tasks, options, and line items
 *
 * @param scan The current scan with changes to revert
 * @param baselineScan The baseline scan to revert to
 * @returns A new scan with all scope changes reverted
 */
export const getScopeRevertedScan = (
  scan: ScanRootOptimized,
  baselineScan: ScanRootOptimized,
  now?: number,
): ScanRootOptimized => {
  const scopeForChildren = getScopeForScanPartChildren(scan);
  const revert = getUpdatedScan(
    scan,
    {
      ...getRevertScanPartUpdates(baselineScan),
      defaultContractorsByTrade: baselineScan.defaultContractorsByTrade,
      groups: getScopeRevertedChildren(
        scan.groupsById,
        baselineScan.groups,
        (group, baselineGroup) => getScopeRevertedGroup(group, baselineGroup, scopeForChildren, now),
      ),
    },
    now,
  );

  // double check that we successfully reverted all scope changes
  if (scanRootHasScopeChanges(revert, baselineScan)) {
    const deoptimizedScan = getDeoptimizedScan(revert);
    const deoptimizedBaselineScan = getDeoptimizedScan(baselineScan);
    const deoptimizedRevert = getDeoptimizedScan(revert);
    console.error('Revert failed. Reverted Scan still has scope changes compared to baseline.', {
      deoptimizedBaselineScan,
      deoptimizedScan,
      deoptimizedResult: deoptimizedRevert,
      revert,
      baselineScan,
      scan,
    });
    const reoptimizedRevert = getOptimizedScan(deoptimizedRevert, revert.template);
    const reoptimizedBaselineScan = getOptimizedScan(deoptimizedBaselineScan, baselineScan.template);
    console.error('After reoptimization, baseline and revert still have scope changes:', scanRootHasScopeChanges(reoptimizedRevert, reoptimizedBaselineScan), {
      revertReducedValues: revert.optimized.reducedValues,
      baselineReducedValues: baselineScan.optimized.reducedValues,
    });
    const patchesForward = compare(stripUpdatedAt(deoptimizedBaselineScan), stripUpdatedAt(deoptimizedRevert));
    console.error('Patches forward:', patchesForward);
    const patchesBackward = compare(stripUpdatedAt(deoptimizedRevert), stripUpdatedAt(deoptimizedBaselineScan));
    console.error('Patches backward:', patchesBackward);
    throw new Error('Revert failed. Reverted Scan still has real changes compared to baseline.');
  }
  return revert;
};
